import React, { useState } from "react";
import {
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import {
  exportDashboardCacheAsCsv,
  exportDashboardCacheAsExcel,
} from "../../utils/exports/exportDashboardCache";

/**
 * DashboardExportButton
 *
 * A button that lets the user download dashboard cached-stream data as:
 *   - Individual CSV files (one per view / data stream)
 *   - A single Excel file with one worksheet per view / data stream
 *
 * Props:
 *   cacheByView  {object}  The cacheByView object returned by useCacheStreamRouter
 *   fileBaseName {string}  Optional base name used for the Excel file and CSV prefix
 *                          (defaults to "dashboard_export")
 */
const DashboardExportButton = ({ cacheByView, fileBaseName = "dashboard_export" }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef(null);

  const hasData =
    cacheByView &&
    Object.values(cacheByView).some((v) => (v?.rows?.length ?? 0) > 0);

  const handleCsv = () => {
    exportDashboardCacheAsCsv(cacheByView, fileBaseName);
    setOpen(false);
  };

  const handleExcel = () => {
    exportDashboardCacheAsExcel(cacheByView, fileBaseName);
    setOpen(false);
  };

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Export dashboard data">
        <span>
          <IconButton
            color="primary"
            ref={anchorRef}
            onClick={handleToggle}
            disabled={!hasData}
            aria-label="Export dashboard data"
          >
            <FileDownloadRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Popper
        sx={{ zIndex: 1300 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-end" ? "right top" : "right bottom",
            }}
          >
            <Paper elevation={4}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  <MenuItem onClick={handleCsv}>
                    Download CSV (separate files per datasource)
                  </MenuItem>
                  <MenuItem onClick={handleExcel}>
                    Download Excel (all datasources as tabs)
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default DashboardExportButton;
